import { CopyObjectCommand, DeleteObjectCommand, GetObjectCommand, ListObjectsV2Command, PutObjectCommand } from '@aws-sdk/client-s3';
import { getS3Client } from '../utilities/CommonUtilities';
import { S3Location } from '../components/context/AppContextModels';
import { logger } from '../logger';
import { ENVIRONMENT_VARIABLES } from '../constants/EnvironmentConstants';

const stage = ENVIRONMENT_VARIABLES.env.Stage;
const normalizedStage = stage.toLowerCase() === 'dev' ? 'alpha' : stage.toLowerCase();
const BUCKET_NAME = `finsuite-app-logos-${normalizedStage}`;
const REGION = ENVIRONMENT_VARIABLES.env.Region || 'us-west-2';
const MAX_FILE_SIZE = 1024 * 1024; // 1 MB
const ACCEPTED_TYPES = ['image/svg+xml', 'image/png'];

export interface TempUploadResult {
  success: boolean;
  tempS3Location?: S3Location;
  error?: string;
}

export interface SharedLogoItem {
  key: string;
  filename: string;
  lastModified: Date | undefined;
  size: number | undefined;
}

/**
 * Service for managing application logo uploads.
 *
 * Uses a two-phase upload pattern:
 * 1. During wizard Step 1: upload to `tmp-logos/{sessionId}/{filename}` — validates immediately
 * 2. On wizard submit: move from tmp path to final `app-logos/{applicationId}/{filename}`
 *
 * This ensures S3 errors surface early in the wizard, and the final path
 * is only written after a successful GraphQL mutation.
 */
export class AppLogoService {
  /**
   * Validates file constraints before upload.
   */
  static validateFile(file: File): string | null {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      return 'Invalid file type. Accepted formats: SVG, PNG.';
    }
    if (file.size > MAX_FILE_SIZE) {
      return `File size exceeds 1 MB limit (${(file.size / 1024).toFixed(0)} KB).`;
    }
    return null;
  }

  /**
   * Phase 1: Upload logo to a temporary S3 path.
   * Called during wizard Step 1 when user selects a file.
   */
  static async uploadToTemp(file: File, sessionId: string): Promise<TempUploadResult> {
    const validationError = this.validateFile(file);
    if (validationError) {
      return { success: false, error: validationError };
    }

    try {
      const s3Client = getS3Client();
      if (!s3Client) {
        return { success: false, error: 'Unable to initialize S3 client. Please ensure you are logged in.' };
      }

      const sanitizedName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const tempKey = `tmp-logos/${sessionId}/${sanitizedName}`;
      const buffer = new Uint8Array(await file.arrayBuffer());

      await s3Client.send(
        new PutObjectCommand({
          Bucket: BUCKET_NAME,
          Key: tempKey,
          Body: buffer,
          ContentType: file.type
        })
      );

      return {
        success: true,
        tempS3Location: {
          bucket: BUCKET_NAME,
          key: tempKey,
          region: REGION,
          contentType: file.type
        }
      };
    } catch (error: any) {
      logger.error('Failed to upload logo to temp path', { sessionId, error: error.message });
      return { success: false, error: 'Failed to upload logo. Please try again.' };
    }
  }

  /**
   * Phase 2: Copy logo from temp path to final app-specific path.
   * Called after successful GraphQL mutation on wizard submit.
   * The temp file is NOT deleted here — caller should delete it after
   * the DB record is successfully updated with the final S3 location.
   */
  static async copyToFinal(tempS3Location: S3Location, applicationId: string): Promise<S3Location> {
    const s3Client = getS3Client();
    if (!s3Client) {
      throw new Error('Unable to initialize S3 client.');
    }

    const filename = tempS3Location.key.split('/').pop()!;
    const finalKey = `app-logos/${applicationId}/${filename}`;

    await s3Client.send(
      new CopyObjectCommand({
        Bucket: BUCKET_NAME,
        CopySource: `${BUCKET_NAME}/${tempS3Location.key}`,
        Key: finalKey,
        MetadataDirective: 'REPLACE',
        ContentType: tempS3Location.contentType
      })
    );

    return {
      bucket: BUCKET_NAME,
      key: finalKey,
      region: REGION,
      contentType: tempS3Location.contentType
    };
  }

  /**
   * @deprecated Use copyToFinal + deleteLogo instead for atomic safety.
   */
  static async moveToFinal(tempS3Location: S3Location, applicationId: string): Promise<S3Location> {
    const finalLocation = await this.copyToFinal(tempS3Location, applicationId);
    await this.deleteLogo(tempS3Location);
    return finalLocation;
  }

  /**
   * Delete an app-specific logo from S3.
   * Used when switching from app-specific to bundled logo, or when replacing.
   */
  static async deleteLogo(s3Location: S3Location): Promise<void> {
    try {
      const s3Client = getS3Client();
      if (!s3Client) return;

      await s3Client.send(
        new DeleteObjectCommand({
          Bucket: s3Location.bucket,
          Key: s3Location.key
        })
      );
    } catch (error: any) {
      logger.error('Failed to delete logo from S3', { key: s3Location.key, error: error.message });
    }
  }

  /**
   * Returns a blob URL for an existing logo in S3 for preview.
   */
  static async getLogoPreviewUrl(s3Location: S3Location): Promise<string | null> {
    try {
      const s3Client = getS3Client();
      if (!s3Client) return null;

      const response = await s3Client.send(new GetObjectCommand({ Bucket: s3Location.bucket, Key: s3Location.key }));
      if (!response.Body) return null;

      const bodyBytes = await response.Body.transformToByteArray();
      const arrayBuffer = new ArrayBuffer(bodyBytes.length);
      new Uint8Array(arrayBuffer).set(bodyBytes);
      const blob = new Blob([arrayBuffer], { type: s3Location.contentType || 'image/png' });
      return URL.createObjectURL(blob);
    } catch (error: any) {
      logger.error('Failed to fetch logo preview from S3', { key: s3Location.key, error: error.message });
      return null;
    }
  }

  // ─── Shared Logo Library ────────────────────────────────────────────────────

  static readonly SHARED_PREFIX = 'shared-logos/';

  /**
   * Lists all logos in the shared library (shared-logos/ prefix).
   */
  static async listSharedLogos(): Promise<SharedLogoItem[]> {
    try {
      const s3Client = getS3Client();
      if (!s3Client) return [];

      const items: SharedLogoItem[] = [];
      let continuationToken: string | undefined;

      do {
        const response = await s3Client.send(
          new ListObjectsV2Command({
            Bucket: BUCKET_NAME,
            Prefix: this.SHARED_PREFIX,
            ContinuationToken: continuationToken
          })
        );

        response.Contents?.forEach((obj) => {
          if (!obj.Key || obj.Key === this.SHARED_PREFIX) return;
          items.push({
            key: obj.Key,
            filename: obj.Key.replace(this.SHARED_PREFIX, ''),
            lastModified: obj.LastModified,
            size: obj.Size
          });
        });

        continuationToken = response.IsTruncated ? response.NextContinuationToken : undefined;
      } while (continuationToken);

      return items;
    } catch (error: any) {
      logger.error('Failed to list shared logos', { error: error.message });
      throw error;
    }
  }

  /**
   * Uploads a logo to the shared library.
   */
  static async uploadSharedLogo(file: File): Promise<{ success: boolean; item?: SharedLogoItem; error?: string }> {
    const validationError = this.validateFile(file);
    if (validationError) return { success: false, error: validationError };

    try {
      const s3Client = getS3Client();
      if (!s3Client) return { success: false, error: 'Unable to initialize S3 client.' };

      const sanitizedName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const key = `${this.SHARED_PREFIX}${sanitizedName}`;
      const buffer = new Uint8Array(await file.arrayBuffer());

      await s3Client.send(
        new PutObjectCommand({
          Bucket: BUCKET_NAME,
          Key: key,
          Body: buffer,
          ContentType: file.type
        })
      );

      return {
        success: true,
        item: { key, filename: sanitizedName, lastModified: new Date(), size: file.size }
      };
    } catch (error: any) {
      logger.error('Failed to upload shared logo', { error: error.message });
      return { success: false, error: 'Failed to upload logo. Please try again.' };
    }
  }

  /**
   * Deletes a logo from the shared library.
   */
  static async deleteSharedLogo(key: string): Promise<void> {
    await this.deleteLogo({ bucket: BUCKET_NAME, key, region: REGION, contentType: '' });
  }

  /**
   * Builds an S3Location for a shared logo filename.
   * Used by AppLogo component to render shared-library logos.
   */
  static buildSharedLogoS3Location(filename: string): S3Location {
    return {
      bucket: BUCKET_NAME,
      key: `${this.SHARED_PREFIX}${filename}`,
      region: REGION,
      contentType: filename.endsWith('.svg') ? 'image/svg+xml' : 'image/png'
    };
  }
}
